<template>
  <div class="mainContainer">
    <!-- Header avec titre et description -->
    <div class="row">
      <div class="devtools-header-container">
        <div class="devtools-logo-container">
          <div class="devtools-icon">🛠️</div>
        </div>
        <div class="devtools-title-container">
          <h1>Dev Tools</h1>
          <p class="description">
            Advanced tools for test data generation and system maintenance
            <span class="access-badge">Developer Access</span>
          </p>
        </div>
        <div class="devtools-actions-container">
          <!-- Future actions can be added here -->
        </div>
      </div>
    </div>

    <!-- Sub-tabs navigation -->
    <div class="dev-tabs-nav">
      <button 
        class="dev-tab-btn"
        :class="{ active: activeDevTab === 'seed' }"
        @click="activeDevTab = 'seed'"
      >
        <i class="fas fa-seedling"></i>
        Data Generator
      </button>
      
      <button 
        class="dev-tab-btn"
        :class="{ active: activeDevTab === 'explorer' }"
        @click="activeDevTab = 'explorer'"
      >
        <i class="fas fa-search"></i>
        Data Explorer
      </button>
      
      <button 
        class="dev-tab-btn"
        :class="{ active: activeDevTab === 'health' }"
        @click="activeDevTab = 'health'"
      >
        <i class="fas fa-heartbeat"></i>
        System Health
      </button>
    </div>

    <!-- Sub-tab content -->
    <div class="dev-content">
      <SeedGenerator v-if="activeDevTab === 'seed'" />
      <DataExplorer v-if="activeDevTab === 'explorer'" />
      <SystemHealth v-if="activeDevTab === 'health'" />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import SeedGenerator from './devTools/SeedGenerator.vue';
import DataExplorer from './devTools/DataExplorer.vue';
import SystemHealth from './devTools/SystemHealth.vue';

// Reactive state
const activeDevTab = ref('seed'); // Starts on data generator
</script>

<style scoped>
/* Main container - standardized with other tabs */
.mainContainer {
  padding: 20px;
  background-color: #F8F9FA;
}

/* Header layout for DevTools - following pattern of other tabs */
.devtools-header-container {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: nowrap;
  padding: 1rem 0;
  margin-bottom: 20px;
}

.devtools-logo-container {
  flex-shrink: 0;
  width: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.devtools-icon {
  font-size: 4rem;
  filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.1));
}

.devtools-title-container {
  flex-grow: 1;
  min-width: 200px;
}

.devtools-title-container h1 {
  font-size: 2em;
  margin: 0 0 5px 0;
  white-space: nowrap;
  color: #2c3e50;
}

.description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
}

.access-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 8px;
}

.devtools-actions-container {
  flex-shrink: 0;
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Sub-tabs navigation - more subtle and integrated */
.dev-tabs-nav {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 25px;
  background: white;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
}

.dev-tab-btn {
  flex: 1;
  max-width: 250px;
  background: transparent;
  border: 1px solid transparent;
  padding: 12px 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #495057;
  border-radius: 6px;
  font-size: 0.9rem;
}

.dev-tab-btn:hover {
  background: #f8f9fa;
  color: #667eea;
  border-color: #dee2e6;
}

.dev-tab-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: transparent;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.dev-tab-btn i {
  margin-right: 6px;
  font-size: 1rem;
}

/* Content area - standardized */
.dev-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
  min-height: 500px;
  overflow: hidden;
}

/* Responsive - following standard pattern */
@media (max-width: 768px) {
  .devtools-header-container {
    flex-wrap: wrap;
  }
  
  .devtools-title-container {
    min-width: 100%;
    order: 2;
    text-align: center;
  }
  
  .devtools-logo-container {
    order: 1;
  }
  
  .devtools-actions-container {
    order: 3;
    min-width: 100%;
    justify-content: center;
    margin-top: 10px;
  }
  
  .dev-tabs-nav {
    flex-direction: column;
    gap: 8px;
  }
  
  .dev-tab-btn {
    max-width: none;
  }
  
  .mainContainer {
    padding: 15px;
  }
  
  .description {
    text-align: left;
  }
}
</style>